/**
 * Small parts of a home tile (#2640): the requests ring, the needs-you
 * chips and the one legend under "Your teams". Icons and numbers, one short
 * word at most; the full meaning is in `aria-label` and `title`.
 */
import {
  ArrowLeftRight,
  CircleCheck,
  FileText,
  Inbox,
  ListChecks,
  type LucideIcon,
  Moon,
  Plane,
  Sun,
} from "lucide-react";
import { Link } from "react-router-dom";

import { cn } from "../../lib/cn";
import type { NeedChip, NeedKind } from "./homeOverview";

const CHIP_ICON: Record<NeedKind, LucideIcon> = {
  leave: Plane,
  swap: ArrowLeftRight,
  message: Inbox,
  task: ListChecks,
};

const CHIP_TONE: Record<NeedKind, string> = {
  leave: "bg-warning-soft text-warning",
  swap: "bg-warning-soft text-warning",
  message: "bg-accent-soft text-accent-fg",
  task: "bg-accent-soft text-accent-fg",
};

/** The needs-you chips, each a link to that list on that team; or "All clear". */
export function NeedsChips({
  chips,
  hrefFor,
}: {
  chips: readonly NeedChip[];
  hrefFor: (chip: NeedChip) => string;
}) {
  if (chips.length === 0) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-sm text-fg-subtle"
        data-testid="home-all-clear"
      >
        <CircleCheck className="h-[18px] w-[18px] text-success" aria-hidden="true" />
        All clear
      </span>
    );
  }
  return (
    <ul className="flex flex-wrap justify-end gap-2" data-testid="home-need-chips">
      {chips.map((chip) => {
        const Icon = CHIP_ICON[chip.kind];
        return (
          <li key={chip.kind}>
            <Link
              to={hrefFor(chip)}
              aria-label={chip.label}
              title={chip.label}
              data-testid={`home-chip-${chip.kind}`}
              className={cn(
                "relative z-10 inline-flex h-8 items-center gap-1.5 rounded-[10px] px-2.5 text-sm font-medium transition-[filter] hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
                CHIP_TONE[chip.kind],
              )}
            >
              <Icon className="h-[15px] w-[15px]" aria-hidden="true" />
              <b className="font-semibold">{chip.count}</b> <span>{chip.word}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/** "14/18 requests in" with a progress ring. */
export function RequestsRing({ submitted, total }: { submitted: number; total: number }) {
  const r = 18;
  const c = 2 * Math.PI * r;
  const f = total > 0 ? Math.min(1, submitted / total) : 0;
  return (
    <span
      className="inline-flex items-center gap-2.5 text-[12.5px] text-fg-subtle"
      data-testid="home-requests-ring"
    >
      <svg width="40" height="40" viewBox="0 0 48 48" aria-hidden="true" className="shrink-0">
        <circle cx="24" cy="24" r={r} fill="none" strokeWidth="6" className="stroke-bg-subtle" />
        <circle
          cx="24"
          cy="24"
          r={r}
          fill="none"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={`${c * f} ${c}`}
          transform="rotate(-90 24 24)"
          className="stroke-accent"
        />
      </svg>
      <span className="flex flex-wrap gap-x-1">
        <b className="whitespace-nowrap text-[13.5px] font-semibold text-fg">
          {submitted}/{total}
        </b>{" "}
        <span className="whitespace-nowrap">requests in</span>
      </span>
    </span>
  );
}

/** "Requests sent" with a tick (a member who has sent theirs). */
export function RequestsSent() {
  return (
    <span
      className="inline-flex items-center gap-1.5 text-sm text-fg-subtle"
      data-testid="home-requests-sent"
    >
      <CircleCheck className="h-[18px] w-[18px] text-success" aria-hidden="true" />
      Requests sent
    </span>
  );
}

/** The one legend under "Your teams": roster states, then shift and leave icons. */
export function HomeLegend() {
  const item = "inline-flex items-center gap-1";
  return (
    <ul
      className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[11.5px] text-fg-subtle"
      aria-label="Calendar key"
      data-testid="home-legend"
    >
      <li className={item}>
        <span
          className="h-[11px] w-[11px] rounded-[3px] border border-accent/30 bg-accent-soft"
          aria-hidden="true"
        />
        <CircleCheck className="h-[13px] w-[13px]" aria-hidden="true" />
        Published
      </li>
      <li className={item}>
        <span
          className="h-[11px] w-[11px] rounded-[3px] border border-dashed border-accent/60"
          aria-hidden="true"
        />
        <FileText className="h-[13px] w-[13px]" aria-hidden="true" />
        Draft
      </li>
      <li className={item}>
        <span
          className="h-[11px] w-[11px] rounded-[3px] border border-border bg-bg-subtle"
          aria-hidden="true"
        />
        Not solved
      </li>
      <li className="h-3.5 w-px bg-border" aria-hidden="true" />
      <li className={item}>
        <Sun className="h-[13px] w-[13px]" aria-hidden="true" />
        Day
      </li>
      <li className={item}>
        <Moon className="h-[13px] w-[13px]" aria-hidden="true" />
        Night
      </li>
      <li className={item}>
        <Plane className="h-[13px] w-[13px]" aria-hidden="true" />
        Leave
      </li>
    </ul>
  );
}
